import {
  allows as apiAllows,
  current as apiCurrent,
  publish,
  subscribe as apiSubscribe,
} from './consent-api.js';
import { allowHosts, startNetBlocking } from './net-blocking.js';
import { noticeFor } from './notice.js';
import { allBlockHosts, hostMap, release, startBlocking } from './prior-blocking.js';
import { localOnly, rayConsent, type Recorder } from './recorder.js';
import { needsPriorConsent, regimeFor, showsBanner } from './regime.js';
import { allows, decide, load, needsDecision, save } from './state.js';
import { BannerUi } from './ui.js';
import type { BannerConfig, ConsentState, Regime } from './types.js';

export type { BannerConfig, ConsentState, Purpose, Regime } from './types.js';
export type { Recorder } from './recorder.js';
export { all, changesOnly, localOnly, rayConsent } from './recorder.js';
export type { Notice } from './notice.js';
export { blockedCalls } from './net-blocking.js';

export interface InitOptions extends BannerConfig {
  /**
   * 방문자 국가 (ISO 3166-1 alpha-2). **모르면 생략한다** — 가장 엄격한
   * 쪽(GDPR)으로 간다.
   *
   * 사이트가 이미 국가를 알고 있으면(에지 헤더 등) 넘겨 준다. 배너가
   * 스스로 알아내려고 **외부에 물어보지 않는다** — 동의 전에 제3자로
   * 나가는 것을 막는 물건이 스스로 그러면 안 된다.
   */
  readonly country?: string | null;

  readonly lang?: string;

  /** 동의를 어딘가에 기록한다. 기본은 **아무 데도 보내지 않는다**. */
  readonly recorder?: Recorder;

  /** 선택이 정해질 때마다 부른다. 허용된 목적만 켜는 데 쓴다. */
  readonly onChange?: (state: ConsentState) => void;
}

export interface BannerHandle {
  /** 지금 선택 상태. 아직 안 정했으면 `null`. */
  state(): ConsentState | null;
  /** 그 목적이 허용됐는가. **필수 목적은 늘 참이다.** */
  allows(purposeId: string): boolean;
  /** 설정 화면을 연다. 사이트가 자기 링크에 걸 수 있다. */
  openPreferences(): void;
  /**
   * 선택을 구독한다. **이미 정해져 있으면 즉시 한 번 부른다** — 호출
   * 순서를 신경 쓰지 않아도 된다.
   */
  subscribe(fn: (state: ConsentState) => void): () => void;
  /** 붙인 것을 걷는다 (테스트·화면 전환용). */
  destroy(): void;
}

/**
 * 배너를 켠다.
 *
 * ## 🔴 가능한 한 일찍 부른다
 *
 * 사전 차단은 **요청이 나가기 전에** 걸려야 한다. 브라우저는 태그를 만나는
 * 즉시 요청을 보내므로, 이 호출이 늦으면 **막으려던 것이 이미 나간 뒤**다.
 * 문서 `<head>` 의 앞쪽에 두고, 번들러가 아래로 옮기지 않는지 확인한다.
 *
 * @example
 *   const banner = init({
 *     purposesVersion: 'v1',
 *     defaultLang: 'ko',
 *     policyUrl: { ko: 'https://example.com/cookie' },
 *     purposes: [
 *       { id: 'necessary', label: { ko: '필수' }, essential: true },
 *       { id: 'analytics', label: { ko: '분석' }, hosts: ['sentry.io'] },
 *       { id: 'fonts', label: { ko: '웹폰트' }, hosts: ['fonts.googleapis.com'] },
 *     ],
 *     country: 'DE',
 *     onChange: (s) => { if (s.choices['analytics'] === 'granted') startAnalytics(); },
 *   });
 */
/**
 * 이미 켠 배너. **두 번 켜지 않기 위해** 들고 있다.
 *
 * 🔴 두 번 켜면 `appendChild`·`fetch` 가 겹쳐 패치되고, 되돌리기가 바깥 한
 * 겹만 벗겨 **원본이 영영 안 돌아온다.** 화면에도 배너가 둘 뜬다.
 *
 * 번들이 두 번 실려 모듈이 둘이면 이 변수도 둘이라 막지 못한다. 그래서
 * 패치 쪽에도 **「우리가 올린 바로 그 함수인가」** 가드를 따로 뒀다 —
 * 그쪽은 전역을 보므로 모듈이 둘이어도 걸린다.
 */
let active: BannerHandle | null = null;

export function init(options: InitOptions): BannerHandle {
  if (active !== null) {
    // 조용히 무시하지 않는다. 두 번 부르는 것은 대개 실수이고, 말하지
    // 않으면 「설정이 왜 안 먹지」로 한참 헤맨다.
    if (typeof console !== 'undefined') {
      console.warn(
        '[ray-cookie-banner] 이미 켜져 있다 — 두 번째 init 은 무시한다. ' +
          '설정을 바꾸려면 destroy() 후 다시 켠다.',
      );
    }
    return active;
  }

  const config: BannerConfig = options;
  const regime = regimeFor(options.country);
  const lang = options.lang ?? options.defaultLang;
  const recorder = options.recorder ?? localOnly;

  let current = load();
  const undecided = needsDecision(current, config);

  // ── 차단을 먼저 건다 ──
  //
  // **아직 정하지 않았고, 그 규제가 사전 동의를 요구할 때만** 막는다.
  // CCPA 는 옵트아웃이라 거부 전까지 허용되므로 막지 않는다 — 막으면
  // 규제가 요구하지 않는 것을 우리가 깨뜨리는 셈이다.
  const blocking = undecided && needsPriorConsent(regime);
  const restore = blocking ? startBlocking(config) : () => undefined;
  // 태그가 아니라 **코드로 나가는 것**도 막는다. 요즘 추적기는 번들 안에서
  // 초기화되고 `fetch`·`sendBeacon` 으로 보내므로 속성을 뗄 요소가 없다.
  //
  // 이것은 **안전망**이다. 주 수단은 소비 측이 `subscribe` 로 동의 상태를
  // 보고 **초기화 자체를 미루는 것**이다 — 네트워크만 막으면 라이브러리는
  // 「보냈다」고 믿는 상태가 되고 전역 오류 처리기 같은 부수 효과는 그대로다.
  const restoreNet = blocking ? startNetBlocking(allBlockHosts(config)) : () => undefined;

  /**
   * `chosen` 은 **이용자가 실제로 골랐는가**다. 규제가 묻지 않는 곳의 자동
   * 허용은 동의가 아니므로 기록하지 않는다 (명세 §2-1).
   */
  const apply = (state: ConsentState, chosen: boolean): void => {
    current = state;
    save(state);
    // 🔴 **허용된 목적의 자원만 되살린다.**
    //
    // 「하나라도 허용되면 전부 푼다」로 두면 **모두 거부해도 전부 나간다** —
    // 필수 목적은 늘 허용이라 그 조건이 언제나 참이기 때문이다. 실제로
    // 그렇게 짰다가 거부 경로를 찍어 보고 잡았다. 이용자는 거부했다고
    // 믿는데 실제로는 전부 나가는, 가장 나쁜 고장이다.
    //
    // 거부한 목적의 자원은 보류 목록에 **그대로 남는다.** 나중에 설정을
    // 바꾸면 그때 풀린다.
    if (blocking) {
      const map = hostMap(config);
      const allowed = Object.entries(state.choices)
        .filter(([, c]) => c === 'granted')
        .flatMap(([id]) => map[id] ?? []);
      release(allowed);
      // 네트워크 차단도 **허용된 것만** 푼다. 거부한 목적의 호스트는
      // 계속 막힌 채로 둔다.
      allowHosts(allowed);
    }
    restore();
    // 구독자에게 알린다. **`onChange` 보다 먼저** 부른다 — 사이트가 넘긴
    // 콜백이 터져도 다른 구독자는 받아야 한다.
    publish(state);
    options.onChange?.(state);
    // **기록 실패가 화면을 막지 않는다.** 이용자는 이미 선택했고 그 선택은
    // 로컬에 저장돼 즉시 유효하다.
    if (chosen) void recorder.record(state, noticeFor(config, lang, state.regime)).catch(() => false);
  };

  let ui: BannerUi | null = null;
  const ensureUi = (): BannerUi => {
    ui ??= new BannerUi(config, lang, {
      onDecide: (picks) => {
        apply(decide(config, regime, picks, new Date(), current), true);
        // 🔴 선택하면 화면을 비우므로 **설정 버튼도 같이 지워진다.** 다시
        //    그리지 않으면 새로고침 전까지 철회할 길이 없다 — SPA 는 페이지를
        //    옮겨도 새로고침이 없어 그 방문 내내 없다. 운영에서 실제로 그랬다.
        ui?.showPreferencesButton(regime);
      },
    });
    return ui;
  };

  if (showsBanner(regime)) {
    const u = ensureUi();
    if (undecided) u.show(regime);
    else u.showPreferencesButton(regime);
  } else if (undecided) {
    // 규제가 요구하지 않는 곳이다. **묻지 않고 허용으로 둔다** — 현행
    // `partner` 설정(`gdprAppliesGlobally: false`)과 같은 동작이다.
    apply(decide(config, regime, {}, new Date(), current), false);
    restore();
  }

  active = {
    state: () => current,
    allows: (id) => allows(current, config, id),
    openPreferences: () => ensureUi().show(regime),
    subscribe: apiSubscribe,
    destroy: () => {
      restore();
      restoreNet();
      ui?.destroy();
      ui = null;
      active = null;
    },
  };
  return active;
}

/** 전역으로도 쓸 수 있게 둔다 — 번들러 없는 사이트를 위해서다. */
declare global {
  interface Window {
    RayCookieBanner?: {
      init: typeof init;
      /** 지금 상태. 아직 안 정했으면 `null`. */
      state: typeof apiCurrent;
      /** 그 목적이 허용됐는가. **미결정은 `false`** 다. */
      allows: typeof apiAllows;
      /** 선택을 구독한다. 이미 정해져 있으면 즉시 한 번 부른다. */
      subscribe: typeof apiSubscribe;
      /** RAYConsent 적재 기록기. `init({ recorder: RayCookieBanner.rayConsent() })` */
      rayConsent: typeof rayConsent;
    };
  }
}

/**
 * 전역으로 둔다.
 *
 * **`init` 을 부른 곳과 상태를 읽는 곳이 다르다.** 배너는 `<head>` 에서
 * 켜지는데 분석·오류 추적은 번들 안쪽에서 초기화되므로, 반환된 handle 을
 * 건네줄 방법이 없다. 그래서 **전역이 실질적인 계약**이다.
 */
if (typeof window !== 'undefined') {
  window.RayCookieBanner = {
    init,
    state: apiCurrent,
    allows: apiAllows,
    subscribe: apiSubscribe,
    rayConsent,
  };
}
